import { useState, useEffect } from 'react'
import { api } from 'helpers'
import servicePaths from '@legacy/common/services/service-paths'

interface IFeatureResponse {
  key: string
  state: 'on' | 'off'
}

interface IFeatureResult {
  enabled: boolean
  /** True when the request errored — `enabled: false` is then a guess, not a real answer */
  failed: boolean
}

// Session-level cache: concurrent mounts asking for the same key share one
// request instead of firing byte-identical GETs (AR-792 review)
const featureRequestCache = new Map<string, Promise<IFeatureResult>>()

const fetchFeatureEnabled = (featureKey: string): Promise<IFeatureResult> => {
  const cached = featureRequestCache.get(featureKey)
  if (cached) return cached

  const request = api
    .get<IFeatureResponse>(servicePaths.flipper.feature(featureKey))
    .then(response => ({ enabled: response.data.state === 'on', failed: false }))
    .catch(error => {
      console.error(`Failed to fetch feature flag '${featureKey}':`, error)
      // Drop the failed promise so a later mount can retry
      featureRequestCache.delete(featureKey)
      return { enabled: false, failed: true }
    })

  featureRequestCache.set(featureKey, request)
  return request
}

export const useSpecificFeature = (
  featureKey: string,
): { enabled: boolean; loading: boolean; failed: boolean } => {
  const [enabled, setEnabled] = useState(false)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!featureKey) return

    let active = true
    fetchFeatureEnabled(featureKey).then(({ enabled: isEnabled, failed: hasFailed }) => {
      if (!active) return
      setEnabled(isEnabled)
      setFailed(hasFailed)
      setLoading(false)
    })

    return () => {
      active = false
    }
  }, [featureKey])

  return { enabled, loading, failed }
}
